import type { MutableRefObject } from 'react';

import * as Sentry from '@sentry/react';
import { CloudFlareError } from 'apps/promo/components/exceptions/CloudFlareError';
import { ForbiddenError } from 'apps/promo/components/exceptions/ForbiddenError';
import { MiddlewareError } from 'apps/promo/components/exceptions/MiddlewareError';
import { convertFieldsToSearchParams } from 'apps/promo/components/form-multi/utils';
import { amplitudeLogout } from 'apps/promo/components/utils/AmplitudeUtils';
import { BAD_DATE, LOCATION, SPRING_REDIRECT_HEADER } from 'apps/promo/constants/Constants';
import { LOGIN, SECURITY_CHECK_URL } from 'apps/promo/constants/UrlConstants';
import { logWarn } from 'apps/promo/helpers/clientLogger';

export type ApiFieldValue = string | number | boolean | Blob | File | null | undefined;

export interface ApiField<TValue = ApiFieldValue> {
    name: string;
    canonicalPath?: string | null;
    value?: TValue;
}

type ApiFieldCollection<TValue = ApiFieldValue> = Record<string, ApiField<TValue>> | ApiField<TValue>[] | null | undefined;

type ControllerRef = MutableRefObject<AbortController | null> | { current?: AbortController | null };

interface CloudFlareActions {
    toggleCloudFlareModal?: (open: boolean, path: string, options: RequestInit) => void;
}

type SearchParamsFieldValue = string | number | boolean;
type FormDataFieldValue = string | number | boolean | Blob | File;

const getPageHeaderValue = (): string => {
    if (typeof window === 'undefined' || !window.location) {
        return '';
    }
    return window.location.pathname + window.location.search;
};

const buildHeaders = (accept: string): Record<string, string> => ({
    Accept: accept,
    'X-Current-Page': getPageHeaderValue(),
});

const getFieldEntries = <TValue>(fields?: ApiFieldCollection<TValue>): ApiField<TValue>[] => {
    if (!fields) {
        return [];
    }
    return Array.isArray(fields) ? fields : Object.values(fields);
};

const appendFieldsToSearchParams = (params: URLSearchParams, fields?: ApiFieldCollection<SearchParamsFieldValue>): void => {
    getFieldEntries(fields).forEach((field) => {
        if (!field?.value) {
            return;
        }

        params.append(field.canonicalPath ?? field.name, String(field.value));
    });
};

const appendFieldsToFormData = (formData: FormData, fields?: ApiFieldCollection<FormDataFieldValue>): void => {
    getFieldEntries(fields).forEach((field) => {
        if (!field?.value) {
            return;
        }

        const key = field.canonicalPath ?? field.name;
        if (field.value instanceof Blob) {
            formData.append(key, field.value);
        } else {
            formData.append(key, String(field.value));
        }
    });
};

const renewAbortSignal = (controllerRef?: ControllerRef | null): AbortSignal | undefined => {
    if (!controllerRef) {
        return undefined;
    }

    if (!controllerRef.current) {
        controllerRef.current = new AbortController();
    }

    try {
        controllerRef.current.abort();
    } catch (error) {
        Sentry.captureException(error, {
            level: 'log',
        });
    } finally {
        controllerRef.current = new AbortController();
    }

    return controllerRef.current.signal;
};

const handleRedirectHeaders = (response: Response): void => {
    const springRedirect = response.headers.get(SPRING_REDIRECT_HEADER);
    if (springRedirect) {
        window.location.replace(springRedirect);
        throw new Error(`redirect to ${springRedirect}`);
    }

    const locationRedirect = response.headers.get(LOCATION);
    if (locationRedirect) {
        window.location.replace(locationRedirect);
        throw new Error(`redirect to ${locationRedirect}`);
    }
};

export const getFullEncodeApiUrl = (path = '/'): string => {
    let url = path;
    if (url.indexOf(SECURITY_CHECK_URL) > 0) {
        url = `${SECURITY_CHECK_URL}`;
    }
    return encodeURI('/api' + url);
};

export const processResponseForCloudFlare = (response: Response, path: string, options: RequestInit, actions?: CloudFlareActions): void => {
    if (response.status === 497 && actions?.toggleCloudFlareModal) {
        actions.toggleCloudFlareModal(true, path, options);
        throw new CloudFlareError('CloudFlare error');
    }
};

export function apiFetchData(
    path?: string,
    fields?: ApiFieldCollection<SearchParamsFieldValue>,
    controllerRef?: ControllerRef | null,
    cloudFlareObj?: CloudFlareActions,
    isText?: true,
): Promise<string>;
export function apiFetchData<TResponse = unknown>(
    path?: string,
    fields?: ApiFieldCollection<SearchParamsFieldValue>,
    controllerRef?: ControllerRef | null,
    cloudFlareObj?: CloudFlareActions,
    isText?: false,
): Promise<TResponse>;
export async function apiFetchData<TResponse = unknown>(
    path = '/',
    fields?: ApiFieldCollection<SearchParamsFieldValue>,
    controllerRef?: ControllerRef | null,
    cloudFlareObj: CloudFlareActions = {},
    isText = false,
): Promise<TResponse | string> {
    const params = new URLSearchParams();
    appendFieldsToSearchParams(params, fields);

    const options: RequestInit = {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        body: params,
        redirect: 'manual',
        headers: buildHeaders('application/json'),
    };
    const signal = renewAbortSignal(controllerRef);
    if (signal) {
        options.signal = signal;
    }

    const encodedUrl = getFullEncodeApiUrl(path);
    const response = await fetch(encodedUrl, options);
    processResponseForCloudFlare(response, encodedUrl, options, cloudFlareObj);
    if (response.status === 403) {
        window.location.replace(LOGIN);
        amplitudeLogout();
        throw new ForbiddenError(`redirect to ${LOGIN}`);
    }
    if (response.type === 'opaqueredirect') {
        window.location.replace(LOGIN);
        throw new Error(`redirect to ${LOGIN}`);
    }

    handleRedirectHeaders(response);

    if (response.status === 500) {
        throw new MiddlewareError(`response status 500 ${response.statusText}`);
    }

    if (isText) {
        return response.text();
    }
    return response.json() as Promise<TResponse>;
}

export function apiFetchDataForMessageBody(path = '/', controllerRef?: ControllerRef | null): Promise<string> {
    const params = new URLSearchParams();
    const options: RequestInit = {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        body: params,
        headers: buildHeaders('application/json'),
    };
    const signal = renewAbortSignal(controllerRef);
    if (signal) {
        options.signal = signal;
    }

    return fetch(getFullEncodeApiUrl(path), options).then((response) => response.text());
}

export function apiFetchFormData<TResponse = unknown>(
    path = '/',
    fields?: ApiFieldCollection<FormDataFieldValue>,
    controllerRef?: ControllerRef | null,
): Promise<TResponse> {
    const params = new FormData();
    appendFieldsToFormData(params, fields);

    const options: RequestInit = {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        body: params,
        headers: buildHeaders('application/json'),
    };
    const signal = renewAbortSignal(controllerRef);
    if (signal) {
        options.signal = signal;
    }

    return fetch(getFullEncodeApiUrl(path), options).then((response) => {
        if (response.status === 403) {
            window.location.replace(LOGIN);
            amplitudeLogout();
            throw new ForbiddenError(`redirect to ${LOGIN}`);
        }
        handleRedirectHeaders(response);

        return response.json() as Promise<TResponse>;
    });
}

export function fetchData<TResponse = unknown>(path = '/', method = 'POST', controllerRef?: ControllerRef | null): Promise<TResponse | null> {
    const options: RequestInit = {
        method: method,
        mode: 'cors',
        credentials: 'include',
        headers: buildHeaders('application/json'),
    };
    const signal = renewAbortSignal(controllerRef);
    if (signal) {
        options.signal = signal;
    }

    return fetch(getFullEncodeApiUrl(path), options)
        .then((response) => {
            if (response.status === 403) {
                window.location.replace(LOGIN);
                amplitudeLogout();
                throw new ForbiddenError(`redirect to ${LOGIN}`);
            }

            handleRedirectHeaders(response);

            return response.json() as Promise<TResponse>;
        })
        .catch((error) => {
            logWarn(error);
            return null;
        });
}

export function apiFetchDataForFlink<TResponse = unknown>(path: string, data?: ApiFieldCollection<SearchParamsFieldValue>): Promise<TResponse> {
    return fetch(getFullEncodeApiUrl(path), {
        method: 'POST',
        mode: 'cors',
        body: convertFieldsToSearchParams(data ?? {}, true),
        credentials: 'include',
    }).then((response) => response.json() as Promise<TResponse>);
}

export function apiFetchDataForApplePay<TResponse = unknown>(path: string, data: Record<string, string | number | boolean>): Promise<TResponse> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(data)) {
        params.append(key, encodeURIComponent(String(value)));
    }
    return fetch(getFullEncodeApiUrl(path), {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        body: params,
        headers: buildHeaders('application/json'),
    }).then((response) => response.json() as Promise<TResponse>);
}

export function apiFetchDataBlob(path: string, addErrorMessageBody: (message: string | null | undefined) => void): Promise<Blob | undefined> {
    return fetch(getFullEncodeApiUrl(path), {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: buildHeaders('application/pdf'),
    }).then((response) => {
        if (response.status === 500) {
            const message = 'Server Error';
            addErrorMessageBody(message);
            throw new MiddlewareError(message);
        }
        const isErrorWithDate = response.headers.has(BAD_DATE);
        if (isErrorWithDate) {
            const message = response.headers.get(BAD_DATE);
            addErrorMessageBody(message);
            return undefined;
        }
        return response.blob();
    });
}

export const changeBrowserUrlWithOutReload = (historyURL: string, json: Record<string, unknown> = {}): void => {
    if (window && window.history && window.history.pushState) {
        window.history.pushState({ html: json, pageTitle: (json as { pageTitle?: string }).pageTitle }, '', historyURL);
    } else {
        window.location.href = historyURL;
    }
};

export const encodeQueryFields = (fields: Record<string, ApiField<SearchParamsFieldValue>>): string => {
    const ret: string[] = [];
    Object.keys(fields).forEach((key) => {
        const field = fields[key];
        ret.push(`${encodeURIComponent(field.canonicalPath ?? field.name)}=${encodeURIComponent(String(field.value))}`);
    });

    return ret.join('&');
};

export const getParams = (search: string): Map<string, string | undefined> => {
    const entries = search
        .slice(1)
        .split('&')
        .map((kv) => {
            const [key, value] = kv.split('=');
            return [key, value] as [string, string | undefined];
        });

    return new Map(entries);
};

export interface StrapiLandingParams {
    language: string;
    country: string;
    operation: string;
    amount?: string | number;
    sourceCountry?: string;
    targetCountry?: string;
    sourceCurrency?: string;
    targetCurrency?: string;
}

export const getStrapiLandingPageData = async <TResponse = unknown>(params: StrapiLandingParams): Promise<TResponse> => {
    const query = new URLSearchParams();
    query.append('lang', params.language);
    query.append('country', params.country);
    query.append('operation', params.operation);
    if (params.amount !== undefined) {
        query.append('amount', String(params.amount));
    }
    if (params.sourceCountry) {
        query.append('sourceCountry', params.sourceCountry);
    }
    if (params.targetCountry) {
        query.append('targetCountry', params.targetCountry);
    }
    if (params.sourceCurrency) {
        query.append('sourceCurrency', params.sourceCurrency);
    }
    if (params.targetCurrency) {
        query.append('targetCurrency', params.targetCurrency);
    }

    const url = `/api/calculator?${query.toString()}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            lang: params.language,
            countrya2: params.country,
        },
    });

    return (await response.json()) as TResponse;
};

export const createInvitedUsersData = async <TResponse = unknown>(
    phoneNumber: string,
    inviteCode: string,
    partnerCode: string,
    lang: string,
    page: string,
): Promise<TResponse> => {
    const response = await fetch('/api/referral/check', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            phone: phoneNumber,
            invite_code: inviteCode,
            partner_code: partnerCode,
            lang,
            page,
        }),
    });

    return (await response.json()) as TResponse;
};
